import { LoadDescriptionT } from 'lib-shared-legacy-types';

import type { PhoneNumber } from 'apis/numbers';
import type { RootReducerStateT } from 'app/reducers/rootReducer';
import { fromNumberInitializationStatus } from 'components/telephony/constants/fromNumberInitialization';

export const phoneCallDisabledTooltipMessages = {
  resolvingDefaultNumber: 'Loading your phone settings…',
  noOutboundNumber:
    'Select a default outbound number in settings to enable phone calls',
  nonOutgoingNumber:
    'The selected number cannot place outbound calls. Pick another number.',
  phoneNotReady: 'The phone is not ready yet',
  blockedMicrophone:
    'Enable the microphone in your browser to enable phone calls',
  activePhoneConversation: 'End your current call before making new calls',
} as const;

export type PhoneCallAvailabilityResult = {
  canMakePhoneCall: boolean;
  // True while the backend default-outbound-number query is still in flight.
  // Consumers should render a loading state (spinner, skeleton) rather than a
  // disabled state with the "no outbound number" tooltip during this window.
  isResolvingDefaultNumber: boolean;
  phoneCallDisabledTooltipText: string;
  selectedFromNumber: PhoneNumber | null;
};

export type PhoneCallAvailabilityOptions = {
  allowConsumedCallbackLoads?: boolean;
};

export function getCurrentOutboundPhoneLoad(
  state: RootReducerStateT,
  { allowConsumedCallbackLoads = false }: PhoneCallAvailabilityOptions = {},
): LoadDescriptionT | undefined {
  return state.presence.presence.presence_status.load_descriptions.find(
    ({ media_type, load_type, conversation_channel, conversation }) => {
      if (media_type !== 'speak') return false;

      if (load_type === 'acw' || load_type === 'nonlive') return false;

      if (conversation_channel === 'pstnphone') return true;

      if (conversation_channel === 'callback') {
        return !(
          allowConsumedCallbackLoads &&
          conversation?.is_callback_consumed === true
        );
      }

      return (
        conversation?.initial_channel === 'callback' &&
        conversation?.is_callback_consumed === false
      );
    },
  );
}

export function getPhoneCallAvailability(
  state: RootReducerStateT,
  options: PhoneCallAvailabilityOptions = {},
): PhoneCallAvailabilityResult {
  const selectedFromNumber = state?.telephony?.selectedFromNumber;
  // `idle` means initialization has not started yet (e.g. the agent has no
  // telephony access, so the init hook never runs). Only `loading` should be
  // surfaced as a transient resolving state to the UI.
  const isResolvingDefaultNumber =
    !selectedFromNumber &&
    state.telephony.fromNumberInitializationStatus ===
      fromNumberInitializationStatus.loading;

  if (!selectedFromNumber) {
    return {
      canMakePhoneCall: false,
      isResolvingDefaultNumber,
      phoneCallDisabledTooltipText: isResolvingDefaultNumber
        ? phoneCallDisabledTooltipMessages.resolvingDefaultNumber
        : phoneCallDisabledTooltipMessages.noOutboundNumber,
      selectedFromNumber,
    };
  }

  if (selectedFromNumber.outgoing_functionality !== true) {
    return {
      canMakePhoneCall: false,
      isResolvingDefaultNumber: false,
      phoneCallDisabledTooltipText:
        phoneCallDisabledTooltipMessages.nonOutgoingNumber,
      selectedFromNumber,
    };
  }

  if (state.telephony.isReady === false) {
    return {
      canMakePhoneCall: false,
      isResolvingDefaultNumber: false,
      phoneCallDisabledTooltipText:
        phoneCallDisabledTooltipMessages.phoneNotReady,
      selectedFromNumber,
    };
  }

  if (state.telephony.isMicrophoneBlocked) {
    return {
      canMakePhoneCall: false,
      isResolvingDefaultNumber: false,
      phoneCallDisabledTooltipText:
        phoneCallDisabledTooltipMessages.blockedMicrophone,
      selectedFromNumber,
    };
  }

  if (
    getCurrentOutboundPhoneLoad(state, options) ||
    (state.telephony.currentCall && state.telephony.currentCall.csid)
  ) {
    return {
      canMakePhoneCall: false,
      isResolvingDefaultNumber: false,
      phoneCallDisabledTooltipText:
        phoneCallDisabledTooltipMessages.activePhoneConversation,
      selectedFromNumber,
    };
  }

  return {
    canMakePhoneCall: true,
    isResolvingDefaultNumber: false,
    phoneCallDisabledTooltipText: '',
    selectedFromNumber,
  };
}
